import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardBody, Col, Container, Row, Spinner, Alert } from 'reactstrap';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import { useAuth } from '../../contexts/AuthContext';
import { useScopedOrders } from '../../hooks/useScopedOrders';
import { OrderNode } from '../../graphql/queries/orders';
import {
  detectOrderType,
  metaArrayToRecord,
  formatOrderTotal,
} from '../../utils/orderLineItemMeta';

function isBookingOrder(order: OrderNode): boolean {
  return (order.lineItems?.nodes ?? []).some((item) => {
    const productName = item.product?.node?.name || '';
    const type = detectOrderType(metaArrayToRecord(item.metaData), productName);
    return type === 'hotel' || type === 'tour';
  });
}

function isTransferOrder(order: OrderNode): boolean {
  return (order.lineItems?.nodes ?? []).some((item) => {
    const productName = item.product?.node?.name || '';
    const type = detectOrderType(metaArrayToRecord(item.metaData), productName);
    return type === 'transfer';
  });
}

const CustomerDashboard: React.FC = () => {
  const { user } = useAuth();
  const { orders, loading, error } = useScopedOrders({ first: 100 });

  const bookingCount = useMemo(() => orders.filter(isBookingOrder).length, [orders]);
  const transferCount = useMemo(() => orders.filter(isTransferOrder).length, [orders]);
  const recentOrders = useMemo(
    () =>
      [...orders]
        .sort((a, b) => {
          const aTime = a.date ? new Date(a.date).getTime() : 0;
          const bTime = b.date ? new Date(b.date).getTime() : 0;
          return bTime - aTime;
        })
        .slice(0, 5),
    [orders]
  );

  const widgets = [
    { title: 'My Orders', count: orders.length, to: '/orders-list', color: 'primary' },
    { title: 'My Bookings', count: bookingCount, to: '/bookings-list', color: 'success' },
    { title: 'My Transfers', count: transferCount, to: '/transfers-list', color: 'info' },
    { title: 'My Invoices', count: orders.length, to: '/invoices-list', color: 'warning' },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Dashboard" breadcrumbItem="My Dashboard" />
        <p className="text-muted mb-4">
          Welcome{user?.name ? `, ${user.name}` : ''}. You can only see your own bookings and orders.
        </p>

        {error && (
          <Alert color="danger">
            {(error as { message?: string }).message || 'Unable to load your data.'}
          </Alert>
        )}

        <Row>
          {widgets.map((widget) => (
            <Col xl={3} md={6} key={widget.title}>
              <Card className="card-h-100">
                <CardBody>
                  <span className="text-muted mb-3 lh-1 d-block">{widget.title}</span>
                  <h4 className="mb-3">
                    {loading ? <Spinner size="sm" /> : widget.count}
                  </h4>
                  <Link to={widget.to} className={`text-${widget.color}`}>
                    View {widget.title.toLowerCase()}
                  </Link>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>

        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <h5 className="mb-3">Recent orders</h5>
                {loading && recentOrders.length === 0 ? (
                  <div className="text-center py-4">
                    <Spinner color="primary" />
                  </div>
                ) : recentOrders.length === 0 ? (
                  <p className="text-muted mb-0">You do not have any orders yet.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-nowrap mb-0">
                      <thead>
                        <tr>
                          <th>Order</th>
                          <th>Date</th>
                          <th>Status</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentOrders.map((order) => (
                          <tr key={order.databaseId}>
                            <td>
                              <Link to={`/orders/${order.databaseId}`}>
                                #{order.orderNumber ?? order.databaseId}
                              </Link>
                            </td>
                            <td>
                              {order.date
                                ? new Date(order.date).toLocaleDateString()
                                : '—'}
                            </td>
                            <td>{order.status || '—'}</td>
                            <td>
                              {formatOrderTotal(order.total, order.currency)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CustomerDashboard;
